import { useState } from "react";
import { locationOptions } from "../data/locations";
import { submitToHubspot } from "../utils/hubspot";

const initialState = {
  firstName: "", lastName: "", email: "", phone: "",
  typeOfBusiness: "", locations: [], message: "", gdpr: false, honeypot: "",
};

const inputClass = "w-full border border-gray-300 text-gray-900 placeholder-gray-400 px-4 py-3 text-base focus:outline-none focus:border-gray-900 transition-colors bg-white";

function ContactForm({ pageUri, pageName }) {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  }

  function handleLocationChange(e) {
    const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
    setForm((prev) => ({ ...prev, locations: selected }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (form.honeypot) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitToHubspot({ firstName: form.firstName, lastName: form.lastName, email: form.email, phone: form.phone, typeOfBusiness: form.typeOfBusiness, locations: form.locations, message: form.message, pageUri, pageName });
      setSuccess(true);
    } catch (err) {
      setError("Something went wrong. Please try again or email us directly at hello@artistic-spaces.co.uk");
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="py-12 text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Thanks for getting in touch!</h3>
        <p className="text-gray-500 text-base">We'll be in touch with you shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input type="text" name="firstName" value={form.firstName} onChange={handleChange} required placeholder="First Name" className={inputClass} />
        <input type="text" name="lastName" value={form.lastName} onChange={handleChange} required placeholder="Last Name" className={inputClass} />
      </div>
      <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="Email" className={inputClass} />
      <div className="flex items-center border border-gray-300 focus-within:border-gray-900 transition-colors bg-white">
        <span className="flex items-center gap-2 px-3 text-base text-gray-500 border-r border-gray-300 py-3 flex-shrink-0">🇬🇧 +44</span>
        <input type="tel" name="phone" value={form.phone} onChange={handleChange} required placeholder="Phone number" className="flex-1 bg-transparent text-gray-900 placeholder-gray-400 px-4 py-3 text-base focus:outline-none" />
      </div>
      <input type="text" name="typeOfBusiness" value={form.typeOfBusiness} onChange={handleChange} required placeholder="Type of business" className={inputClass} />
      <div>
        <label className="block text-base text-gray-700 mb-1">Location</label>
        <p className="text-sm text-gray-400 mb-2">To select multiple locations, hold Cmd or Ctrl.</p>
        <select name="locations" multiple value={form.locations} onChange={handleLocationChange} className="w-full border border-gray-300 text-gray-900 text-base px-4 py-2 focus:outline-none focus:border-gray-900 transition-colors min-h-[120px] bg-white">
          <option value="All locations">All locations</option>
          {locationOptions.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
        </select>
      </div>
      <textarea name="message" value={form.message} onChange={handleChange} required rows={5} placeholder="Message" className={`${inputClass} resize-y`} />
      <div className="flex items-start gap-3">
        <input type="checkbox" name="gdpr" id="gdpr-contact" checked={form.gdpr} onChange={handleChange} required className="mt-1 h-4 w-4 accent-[#8a7d5a]" />
        <label htmlFor="gdpr-contact" className="text-sm text-gray-500 leading-relaxed">
          I agree with the handling of my data in accordance with this enquiry form and the Artistic Spaces{" "}
          <a href="/privacy-policy" className="underline hover:text-gray-900">Privacy Policy</a>
        </label>
      </div>
      <div style={{ position: "absolute", left: "-9999px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
        <input type="text" name="honeypot" value={form.honeypot} onChange={handleChange} tabIndex={-1} autoComplete="off" />
      </div>
      {error && <p className="text-base text-red-500">{error}</p>}
      <button type="submit" disabled={submitting} className="self-start px-8 py-3 bg-[#8a7d5a] text-white text-sm font-semibold tracking-widest uppercase hover:bg-[#7a6d4a] transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed">
        {submitting ? "Submitting..." : "Submit"}
      </button>
    </form>
  );
}

export default function Contact() {
  return (
    <main className="pt-16 md:pt-20">
      <section
        className="relative w-full h-64 md:h-80"
        style={{ backgroundImage: "url('/static/images/hero-contact.webp')", backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-black/40" />
      </section>

      <section className="w-full bg-[#f5f4f0] px-8 md:px-16 lg:px-24 py-12">
        <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 mb-3">Contact us</h1>
        <p className="text-gray-600 text-base leading-relaxed max-w-2xl">
          If you're interested in one of our spaces or are an existing tenant, feel free to reach out to us through any of the methods below.
        </p>
      </section>

      <section className="w-full bg-white px-8 md:px-16 lg:px-24 py-16">
        <div className="flex flex-col md:flex-row gap-16">
          <div className="md:w-5/12">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact information</h2>
            <p className="text-base text-gray-600 mb-6">For all new enquiries, please email us or call, and our dedicated team will be happy to assist.</p>
            <div className="flex flex-col gap-2">
              <a href="mailto:hello@artistic-spaces.co.uk" className="text-base text-gray-800 hover:text-gray-500 transition-colors">hello@artistic-spaces.co.uk</a>
              <a href="tel:02033024266" className="text-base text-gray-800 hover:text-gray-500 transition-colors">0203 302 4266</a>
            </div>
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Enquiry Form</h2>
            <ContactForm pageUri="https://artistic-spaces.co.uk/contact" pageName="Contact Us" />
          </div>
        </div>
      </section>
    </main>
  );
}